import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ENV } from "../../config/env";
import { api } from "../../lib/api";
import AuthImage from "../../components/AuthImage";

const getToken = () => localStorage.getItem("admin_token");

const ALLOWED_TYPES = [
  "image/jpeg", "image/jpg", "image/png", "image/gif",
  "image/webp", "image/bmp", "image/avif", "image/heic", "image/heif",
];

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

function imagePreviewSrc(img) {
  return `/admin/city-images/${img.id}/preview`;
}

const TIME_LABELS = { day: "Day (06:00 – 18:00)", night: "Night (18:00 – 06:00)" };

export default function CityImageEdit() {
  const { batch_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [batch, setBatch] = useState(location.state?.batch || null);
  const [loadingBatch, setLoadingBatch] = useState(!location.state?.batch);
  const [loadError, setLoadError] = useState("");

  // Existing images — track which to keep
  const [keepIds, setKeepIds] = useState([]);

  // New files to add
  const [newFiles, setNewFiles] = useState([]); // [{ id, file, preview }]
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Load batch from API if not passed via state (e.g. page refresh)
  useEffect(() => {
    if (batch) {
      setKeepIds((batch.images || []).map((img) => img.id));
      return;
    }
    (async () => {
      setLoadingBatch(true);
      try {
        const { data } = await api.get(`/admin/city-images/batch/${batch_id}`);
        setBatch(data.data);
        setKeepIds((data.data.images || []).map((img) => img.id));
      } catch {
        setLoadError("Could not load batch. It may have been deleted or you lack access.");
      } finally {
        setLoadingBatch(false);
      }
    })();
  }, [batch_id]);

  // Cleanup new file preview URLs on unmount
  useEffect(() => {
    return () => newFiles.forEach((f) => URL.revokeObjectURL(f.preview));
  }, []);

  const toggleKeep = (id) => {
    setKeepIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const addFiles = (incoming) => {
    const valid = Array.from(incoming).filter((f) => ALLOWED_TYPES.includes(f.type));
    setNewFiles((prev) => {
      const existing = new Set(prev.map((f) => `${f.file.name}|${f.file.size}`));
      const fresh = valid
        .filter((f) => !existing.has(`${f.name}|${f.size}`))
        .map((f) => ({ id: `${f.name}-${f.size}-${Math.random()}`, file: f, preview: URL.createObjectURL(f) }));
      return [...prev, ...fresh];
    });
  };

  const removeNewFile = (id) => {
    setNewFiles((prev) => {
      const removed = prev.find((f) => f.id === id);
      if (removed) URL.revokeObjectURL(removed.preview);
      return prev.filter((f) => f.id !== id);
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (keepIds.length === 0 && newFiles.length === 0) {
      return setError("You must keep at least one existing image or add a new one.");
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      newFiles.forEach((f) => formData.append("files", f.file));
      formData.append("keep_ids", JSON.stringify(keepIds));

      const res = await fetch(`${ENV.API_BASE_URL}/admin/city-images/batch/${batch_id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || "Update failed.");

      newFiles.forEach((f) => URL.revokeObjectURL(f.preview));
      setNewFiles([]);
      navigate("/admin/city-images");
    } catch (err) {
      setError(err.message || "Update failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingBatch) {
    return <div className="p-8 text-center text-gray-400 text-sm">Loading batch…</div>;
  }

  if (loadError) {
    return (
      <div className="p-8 max-w-xl mx-auto text-center">
        <p className="text-red-500 mb-4">{loadError}</p>
        <button
          type="button"
          onClick={() => navigate("/admin/city-images")}
          className="text-sm text-blue-600 underline"
        >
          Back to list
        </button>
      </div>
    );
  }

  const existingImages = batch?.images || [];
  const keptCount = keepIds.length;
  const totalAfter = keptCount + newFiles.length;

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={() => navigate("/admin/city-images")}
          className="text-gray-500 hover:text-gray-700 text-sm"
        >
          ← Back
        </button>
        <h1 className="text-2xl font-bold text-gray-800">Edit City Image Batch</h1>
      </div>

      {/* Batch info — read-only */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-4 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="font-semibold text-gray-800 text-base">{batch?.city_name}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {batch?.city_code} · {batch?.country_name}
            </p>
            <div className="flex flex-wrap gap-1 mt-2">
              {(batch?.iata_codes || []).map((code) => (
                <span key={code} className="bg-white border border-gray-300 text-gray-700 text-xs font-mono px-2 py-0.5 rounded">
                  {code}
                </span>
              ))}
            </div>
          </div>
          <span className={`inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full flex-shrink-0 ${batch?.time_of_day === "day" ? "bg-amber-50 text-amber-700" : "bg-indigo-50 text-indigo-700"}`}>
            {batch?.time_of_day === "day" ? "☀️" : "🌙"} {TIME_LABELS[batch?.time_of_day]}
          </span>
        </div>
        {batch?.review_remark && (
          <div className="mt-3 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 text-xs text-orange-700">
            <span className="font-semibold">Review note:</span> {batch.review_remark}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">

        {/* Existing images */}
        {existingImages.length > 0 && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
              <p className="text-sm font-medium text-gray-700">
                Existing images — remove any image being replaced
              </p>
              <div className="flex gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setKeepIds(existingImages.map((img) => img.id))}
                  className="text-xs border border-gray-300 text-gray-600 px-2.5 py-1 rounded hover:bg-gray-50"
                >
                  Keep all
                </button>
                <button
                  type="button"
                  onClick={() => setKeepIds([])}
                  className="text-xs border border-red-200 text-red-600 px-2.5 py-1 rounded hover:bg-red-50"
                >
                  Replace all
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {existingImages.map((img) => {
                const kept = keepIds.includes(img.id);
                return (
                  <div
                    key={img.id}
                    className={`relative rounded-xl overflow-hidden border-2 transition cursor-pointer ${kept ? "border-blue-500" : "border-gray-200 opacity-40"}`}
                    onClick={() => toggleKeep(img.id)}
                  >
                    <AuthImage
                      src={imagePreviewSrc(img)}
                      alt={img.original_filename}
                      className="w-full h-32 object-cover"
                    />
                    {/* Keep / remove badge */}
                    <div className={`absolute top-1.5 left-1.5 rounded-full w-5 h-5 flex items-center justify-center text-white text-xs font-bold shadow ${kept ? "bg-blue-500" : "bg-gray-400"}`}>
                      {kept ? "✓" : "✕"}
                    </div>
                    <div className="px-2 py-1.5 bg-white">
                      <p className="text-xs text-gray-600 truncate" title={img.original_filename}>
                        {img.original_filename}
                      </p>
                      <p className={`text-xs font-medium ${kept ? "text-blue-600" : "text-red-500"}`}>
                        {kept ? "Keep" : "Remove"}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-400 mt-2">
              {keptCount} of {existingImages.length} existing image{existingImages.length !== 1 ? "s" : ""} kept
            </p>
          </div>
        )}

        {/* Drop zone for new files */}
        <div>
          <p className="text-sm font-medium text-gray-700 mb-1">
            Add replacement images
            <span className="ml-2 text-xs text-gray-400 font-normal">optional — drag-drop or browse</span>
          </p>
          <div
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl px-6 py-7 text-center cursor-pointer transition-colors ${
              isDragging ? "border-blue-400 bg-blue-50" : "border-gray-300 hover:border-blue-400 hover:bg-gray-50"
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = ""; }}
            />
            <p className="font-medium text-gray-700 text-sm">Drop new images here or click to browse</p>
            <p className="text-xs text-gray-400 mt-1">JPG · PNG · GIF · WebP · BMP · AVIF · Max 50 MB each</p>
          </div>
        </div>

        {/* New files grid */}
        {newFiles.length > 0 && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {newFiles.map((item) => (
              <div key={item.id} className="relative rounded-xl overflow-hidden border border-gray-200">
                <img src={item.preview} alt={item.file.name} className="w-full h-32 object-cover" />
                <button
                  type="button"
                  onClick={() => removeNewFile(item.id)}
                  className="absolute top-1.5 right-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shadow transition"
                  title="Remove"
                >
                  ✕
                </button>
                <div className="px-2 py-1.5 bg-white">
                  <p className="text-xs text-gray-600 truncate" title={item.file.name}>{item.file.name}</p>
                  <p className="text-xs text-gray-400">{formatBytes(item.file.size)}</p>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="h-[calc(8rem+52px)] rounded-xl border-2 border-dashed border-gray-300 hover:border-blue-400 hover:bg-gray-50 flex flex-col items-center justify-center gap-1 text-gray-400 hover:text-blue-500 transition"
            >
              <span className="text-2xl">+</span>
              <span className="text-xs">Add more</span>
            </button>
          </div>
        )}

        {/* Summary */}
        {totalAfter > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-700">
            After saving: <span className="font-semibold">{totalAfter} image{totalAfter !== 1 ? "s" : ""}</span> in this batch
            {newFiles.length > 0 && ` (${keptCount} kept + ${newFiles.length} new)`}
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || totalAfter === 0}
          className="w-full bg-[#5dbfef] text-white py-3 rounded-xl font-medium text-sm hover:bg-blue-700 transition disabled:opacity-60"
        >
          {submitting
            ? "Saving…"
            : `Save & Resubmit for Review (${totalAfter} image${totalAfter !== 1 ? "s" : ""})`}
        </button>
      </form>
    </div>
  );
}
